/**
 * Save Queue Manager
 *
 * Serializes save operations to prevent version conflicts.
 * Ensures operations are executed sequentially, with each operation
 * using the latest version information from the previous operation.
 *
 * Design philosophy: keep complexity minimal while preventing version conflicts.
 * We accept that drag position data may be lost on flush failure (the user can
 * re-drag), but we never silently lose data from immediate operations (edit, layout toggle).
 */

export class SaveQueue {
    private queue: Array<{
        operation: () => Promise<any>;
        resolve: (value: any) => void;
        reject: (reason: any) => void;
        operationName?: string;
    }> = [];
    private isProcessing = false;

    /**
     * Enqueue a save operation.
     *
     * @returns Promise that resolves with the operation result, or `undefined`
     *          if this operation was deduplicated (replaced by a newer one with the same name).
     *
     * Deduplication: If `operationName` is provided and `deduplicate` is true,
     * any existing pending (not-yet-executing) operation with the same name
     * will be replaced by this new one. The replaced operation's promise resolves
     * with undefined (skipped). This prevents redundant API calls for operations
     * like layout-toggle where only the latest state matters.
     */
    async enqueue<T>(
        operation: () => Promise<T>,
        operationName?: string,
        deduplicate: boolean = false
    ): Promise<T | undefined> {
        // Deduplication: replace existing pending operation with same name
        if (deduplicate && operationName) {
            const existingIndex = this.queue.findIndex(
                item => item.operationName === operationName
            );
            if (existingIndex !== -1) {
                const existing = this.queue[existingIndex];
                existing.resolve(undefined);
                this.queue.splice(existingIndex, 1);
            }
        }

        return new Promise((resolve, reject) => {
            this.queue.push({ operation, resolve, reject, operationName });
            this.processQueue();
        });
    }

    /**
     * Process the queue sequentially.
     * Ensures operations execute one at a time.
     */
    private async processQueue() {
        if (this.isProcessing || this.queue.length === 0) {
            return;
        }

        this.isProcessing = true;

        while (this.queue.length > 0) {
            const item = this.queue.shift()!;
            try {
                const result = await item.operation();
                item.resolve(result);
            } catch (error) {
                console.error(`[SaveQueue] "${item.operationName || "unnamed"}" failed:`, error);
                item.reject(error);
            }
        }

        this.isProcessing = false;
    }

    getQueueLength(): number {
        return this.queue.length;
    }

    isProcessingQueue(): boolean {
        return this.isProcessing;
    }

    clear(): void {
        const clearedCount = this.queue.length;
        this.queue.forEach(item => {
            item.reject(new Error("Queue cleared"));
        });
        this.queue = [];
        if (clearedCount > 0) {
            console.warn(`[SaveQueue] Queue cleared, ${clearedCount} operations discarded`);
        }
    }
}

/**
 * Batched Save Queue
 *
 * Extends SaveQueue with batched update support for rapid-fire operations (drag positions).
 * - enqueue(): for immediate operations (edit, layout toggle)
 * - collect()/flush(): for batching rapid updates (drag positions)
 *
 * All operations are serialized through the same queue to prevent version conflicts.
 *
 * Batch behavior:
 * - Debounce: waits for inactivity before flushing (default 300ms)
 * - Max wait: forces flush after maximum time even during continuous activity (default 5s)
 * - Lazy snapshot: pendingUpdates are snapshotted at execution time, not at flush time.
 *   This coalesces all collects between flush-enqueue and flush-execute into one API call.
 * - Single pending flush: only one flush can be pending. Additional flush() calls return
 *   the same promise, ensuring all callers wait for actual completion.
 *
 * Acceptable data loss:
 * - On flush failure, failed positions are restored to pendingUpdates for the next flush.
 *   However, if the component unmounts before the next flush, those positions are lost.
 *   This is acceptable because the user can re-drag nodes to the desired positions.
 */
export class BatchedSaveQueue<T> extends SaveQueue {
    private pendingUpdates = new Map<string, T>();
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
    private flushCallback: ((updates: Map<string, T>) => Promise<void>) | null = null;
    private pendingFlushPromise: Promise<void> | null = null;
    private debounceMs: number;
    private maxWaitMs: number;

    constructor(options: {
        debounceMs?: number;
        maxWaitMs?: number;
    } = {}) {
        super();
        this.debounceMs = options.debounceMs ?? 300;
        this.maxWaitMs = options.maxWaitMs ?? 5000;
    }

    onFlush(callback: (updates: Map<string, T>) => Promise<void>): void {
        this.flushCallback = callback;
    }

    collect(key: string, value: T): void {
        this.pendingUpdates.set(key, value);

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
            this.debounceTimer = null;
            this.flush();
        }, this.debounceMs);

        if (!this.maxWaitTimer) {
            this.maxWaitTimer = setTimeout(() => {
                this.maxWaitTimer = null;
                this.flush();
            }, this.maxWaitMs);
        }
    }

    /**
     * Flush all pending batched updates.
     * Uses lazy snapshot — pendingUpdates are captured at execution time.
     * If a flush is already pending, returns the same promise so all callers
     * wait for actual completion (not an immediately-resolved promise).
     */
    flush(): Promise<void> {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        if (this.maxWaitTimer) {
            clearTimeout(this.maxWaitTimer);
            this.maxWaitTimer = null;
        }

        if (!this.flushCallback) return Promise.resolve();
        if (this.pendingFlushPromise) return this.pendingFlushPromise;
        if (this.pendingUpdates.size === 0) return Promise.resolve();

        const callback = this.flushCallback;

        this.pendingFlushPromise = this.enqueue(
            async () => {
                const updates = new Map(this.pendingUpdates);
                if (updates.size === 0) return;

                this.pendingUpdates.clear();

                try {
                    await callback(updates);
                } catch (error) {
                    // Restore failed updates, but preserve newer values
                    // collected during the async callback execution
                    updates.forEach((value, key) => {
                        if (!this.pendingUpdates.has(key)) {
                            this.pendingUpdates.set(key, value);
                        }
                    });
                    throw error;
                }
            },
            "batch-flush"
        ).then(() => {
            // Resolved — noop
        }).catch(error => {
            console.error("[SaveQueue] Batch flush failed:", error);
        }).finally(() => {
            this.pendingFlushPromise = null;
        });

        return this.pendingFlushPromise;
    }

    hasPending(): boolean {
        return this.pendingUpdates.size > 0;
    }

    async destroy(): Promise<void> {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        if (this.maxWaitTimer) {
            clearTimeout(this.maxWaitTimer);
            this.maxWaitTimer = null;
        }
        this.pendingFlushPromise = null;
        if (this.pendingUpdates.size > 0 && this.flushCallback) {
            await this.flush();
        }
        this.clear();
    }
}
